import * as Yup from 'yup'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { FormikProps } from 'formik'

import { Icons } from '../../components/Icon'
import { ImageTransformer } from '../Transformers/ImageTransformer'
import Router from '../../routes/Router'
import routes from '../../routes/definitions'
import { useServiceRequest } from '../../hooks/useServiceRequest'
import { actions, selectors } from '../../redux'
import useUser from '../User/useUser'
import { AddFeedbackModalProps } from '../../components/AddFeedbackModal'
import { Configuration } from '../../rest/types/Configuration'

const useAddFeedbackProps = (
  isAddFeedbackModalVisible: boolean,
  setIsAddFeedbackModalVisible: (bool: boolean) => void,
  setLocked: (bool: boolean) => void,
  offerId: string,
  offerSlug: string,
  configuration: Configuration
): AddFeedbackModalProps => {
  const { t } = useTranslation()
  const { user: userInfo } = useUser()
  const formikRef = useRef<FormikProps<any>>(null)
  const dispatch = useDispatch()
  const updatedFeedback = useSelector(
    selectors.feedbacks.currentUpdatedFeedback
  )

  const resetForm = useCallback(() => {
    dispatch(actions.feedbacks.setCurrentUpdatedFeedback(null))
    formikRef.current?.resetForm()
  }, [dispatch])

  const [response, handleAddFeedback] = useServiceRequest(
    selectors.feedbacks.addFeedback,
    actions.feedbacks.addFeedbackRequest,
    actions.feedbacks.addFeedbackReset
  )

  const [updatedFeedbackResponse, handleUpdatedFeedback] = useServiceRequest(
    selectors.feedbacks.updatedFeedback,
    actions.feedbacks.updatedFeedbackRequest,
    actions.feedbacks.updatedFeedbackReset
  )

  useEffect(() => {
    if (response.complete) {
      const timeout = setTimeout(() => {
        resetForm()
        setIsAddFeedbackModalVisible(false)
        setLocked(false)
        clearTimeout(timeout)
      }, 500)
    }
  }, [response.complete, setIsAddFeedbackModalVisible, setLocked, resetForm])

  useEffect(() => {
    if (updatedFeedbackResponse.complete) {
      const timeout = setTimeout(() => {
        resetForm()
        setIsAddFeedbackModalVisible(false)
        setLocked(false)
        clearTimeout(timeout)
      }, 500)
    }
  }, [
    updatedFeedbackResponse.complete,
    setIsAddFeedbackModalVisible,
    setLocked,
    dispatch,
    resetForm,
  ])

  useEffect(() => {
    if (updatedFeedback && formikRef.current) {
      formikRef.current.setFieldValue('stars', updatedFeedback.rating)
      formikRef.current.setFieldValue('review', updatedFeedback.text)
    }
  }, [updatedFeedback])

  return useMemo(
    () => ({
      avatar: {
        ...(userInfo && {
          photo: ImageTransformer(
            (userInfo?.avatar as any) ||
              (configuration.config.defaultAvatar as any)
          ),
        }),
        link: {
          href: Router.getRouteUrl(routes.account),
        },
        name: userInfo?.firstName,
        surname: userInfo?.lastName,
      },
      formReview: {
        innerRef: formikRef,
        initialValues: {
          stars: 0,
          review: '',
        },
        validationSchema: Yup.object().shape({
          stars: Yup.number()
            .required(t('yulp_error_message_empty_field'))
            .min(1, t('yulp_error_message_min_star'))
            .max(5, t('yulp_error_message_max_star')),
          review: Yup.string().required(t('yulp_error_message_empty_field')),
        }),
        fields: {
          message: {
            label: t('form_field_message_label'),
            placeholder: t('form_field_name_label'),
          },
          starPicker: {
            label: t('form_field_star_picker_label'),
          },
        },
        onSubmit: (values: any) => {
          if (updatedFeedback) {
            handleUpdatedFeedback({
              rating: values.stars?.toString(),
              text: values.review,
              feedback: updatedFeedback.documentId ?? updatedFeedback.id,
            })
          } else {
            handleAddFeedback({
              rating: values.stars?.toString(),
              text: values.review,
              offer: offerId,
              offerSlug: offerSlug,
              userId: userInfo?.id,
            })
          }
        },
        texts: {
          button: updatedFeedback
            ? t('updated_feedback_form_text_button')
            : t('add_feedback_form_text_button'),
        },
        submitButton: {
          isLoading: response.pending || updatedFeedbackResponse.pending,
          disabled: response.pending ? response.pending : response.complete,
        },
      },
      isVisible: isAddFeedbackModalVisible,
      closeIcon: {
        icon: Icons.close,
        width: 24,
        height: 24,
        onClick: () => {
          resetForm()
          setIsAddFeedbackModalVisible(false)
          setLocked(false)
        },
      },
    }),
    [
      userInfo,
      configuration.config.defaultAvatar,
      t,
      updatedFeedback,
      response.pending,
      response.complete,
      updatedFeedbackResponse.pending,
      isAddFeedbackModalVisible,
      handleUpdatedFeedback,
      handleAddFeedback,
      offerId,
      offerSlug,
      resetForm,
      setIsAddFeedbackModalVisible,
      setLocked,
    ]
  )
}

export default useAddFeedbackProps
